import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider, Avatar } from '@mui/material';
import type { ArbitrageOpportunity } from '../../types/opportunity';
import { getExchangeName, getQuoteCurrency } from '../../types/opportunity';
import { formatPrice, formatVolume, formatPercentage } from '../../utils/formatters';
import { calculateProfitBreakdownFromBudget, type ProfitCalculationResult } from '../../utils/profitCalculation';
import { usePriceUpdates } from '../../hooks/usePriceUpdates';
import { useAppSelector } from '../../app/hooks';
import { selectNetworkStatus } from '../../features/networkStatus/networkStatusSlice';
import { getExchangeLogo } from '../../utils/exchangeUrls';

interface ProfitBreakdownDisplayProps {
  opportunity: ArbitrageOpportunity;
  budget: number;
}

export const ProfitBreakdownDisplay: React.FC<ProfitBreakdownDisplayProps> = ({ opportunity, budget }) => {
  const buyExchangeName = getExchangeName(opportunity.buy_exchange);
  const sellExchangeName = getExchangeName(opportunity.sell_exchange);
  const buyQuote = getQuoteCurrency(opportunity.buy_quote);
  const sellQuote = getQuoteCurrency(opportunity.sell_quote);

  // Get current exchange rates and network status
  const prices = usePriceUpdates();
  const networkStatus = useAppSelector(selectNetworkStatus);

  // Store calculation result in state to prevent component from disappearing
  const [calculationResult, setCalculationResult] = useState<ProfitCalculationResult | null>(null);

  // Recalculate whenever opportunity, budget, prices, or network status changes
  // This ensures the dialog updates at the same speed as the opportunity table
  useEffect(() => {
    if (!prices) return;

    const result = calculateProfitBreakdownFromBudget(opportunity, budget, prices, networkStatus);
    setCalculationResult(result);
  }, [prices, opportunity, budget, networkStatus]);

  // Show loading state while prices are being fetched
  if (!calculationResult) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="caption" color="text.secondary">Loading exchange rates...</Typography>
      </Box>
    );
  }

  // Use the exact same calculation logic as ProfitCalculationTooltip
  const {
    volumeBeforeFees,
    maxExecutableVolume,
    maxExecutableVolumeAfterWithdrawal,
    totalBuyCost,
    totalBuyCostInSellQuote,
    buyFeeAmount,
    withdrawalFeeAmount,
    grossSellRevenue,
    sellFeeAmount,
    netSellRevenue,
    netProfit,
    profitPercentage,
    buyTakerFee,
    sellMakerFee,
    withdrawalNetwork,
  } = calculationResult;

  if (volumeBeforeFees === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="caption" color="error.main">Insufficient budget or no orderbook data</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Net Profit Summary First */}
      <Box sx={{ mb: 2, bgcolor: 'rgba(0, 255, 255, 0.05)', p: 1, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
          <Typography variant="caption">Net Profit ({sellQuote}):</Typography>
          <Typography
            variant="caption"
            sx={{
              color: netProfit > 0 ? 'success.main' : 'error.main',
              fontWeight: 'bold'
            }}
          >
            {formatPrice(netProfit, sellQuote[0])}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
          <Typography variant="caption">Profit %:</Typography>
          <Typography
            variant="caption"
            sx={{
              color: profitPercentage > 0 ? 'success.main' : 'error.main',
              fontWeight: 'bold'
            }}
          >
            {formatPercentage(profitPercentage, 2)}
          </Typography>
        </Box>
      </Box>

      {/* Two Column Layout: Buy (left) and Sell (right) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {/* Buy Side (Left Column) */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <Avatar
              src={getExchangeLogo(opportunity.buy_exchange)}
              sx={{
                width: 20,
                height: 20,
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Price:</Typography>
            <Typography variant="caption">{formatPrice(opportunity.buy_price || 0, buyQuote[0])}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Volume:</Typography>
            <Typography variant="caption">{formatVolume(volumeBeforeFees, 8)} {opportunity.base}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Cost:</Typography>
            <Typography variant="caption">{formatPrice(totalBuyCost, buyQuote[0])}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Taker Fee ({(buyTakerFee * 100).toFixed(2)}%):</Typography>
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              -{formatVolume(volumeBeforeFees * buyTakerFee, 8)} {opportunity.base}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">{withdrawalNetwork ? `Withdraw (${withdrawalNetwork})` : 'Withdraw Fee'}:</Typography>
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              -{formatVolume(withdrawalFeeAmount, 8)} {opportunity.base}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', pt: 0.5, borderTop: '1px solid rgba(255, 255, 255, 0.12)' }}>
            <Typography variant="caption">After Fees:</Typography>
            <Typography variant="caption">{formatVolume(maxExecutableVolumeAfterWithdrawal, 8)} {opportunity.base}</Typography>
          </Box>
        </Box>

        {/* Sell Side (Right Column) */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <Avatar
              src={getExchangeLogo(opportunity.sell_exchange)}
              sx={{
                width: 20,
                height: 20,
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Price:</Typography>
            <Typography variant="caption">{formatPrice(opportunity.sell_price || 0, sellQuote[0])}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Volume:</Typography>
            <Typography variant="caption">{formatVolume(maxExecutableVolumeAfterWithdrawal, 8)} {opportunity.base}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Gross:</Typography>
            <Typography variant="caption">{formatPrice(grossSellRevenue, sellQuote[0])}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Taker Fee ({(sellMakerFee * 100).toFixed(2)}%):</Typography>
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              -{formatVolume(maxExecutableVolumeAfterWithdrawal * sellMakerFee, 8)} {opportunity.base}
            </Typography>
          </Box>
          {/* Empty row to align with withdrawal fee on buy side */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, visibility: 'hidden' }}>
            <Typography variant="caption">-</Typography>
            <Typography variant="caption">-</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', pt: 0.5, borderTop: '1px solid rgba(255, 255, 255, 0.12)' }}>
            <Typography variant="caption">Net Revenue:</Typography>
            <Typography variant="caption">{formatPrice(netSellRevenue, sellQuote[0])}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Currency Conversion if needed */}
      {opportunity.quote_conversion_rate !== 1.0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.12)' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
            Currency Conversion
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Rate ({buyQuote} → {sellQuote}):</Typography>
            <Typography variant="caption">{opportunity.quote_conversion_rate.toFixed(6)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Buy Cost in {sellQuote}:</Typography>
            <Typography variant="caption">{formatPrice(totalBuyCostInSellQuote, sellQuote[0])}</Typography>
          </Box>
        </Box>
      )}
    </>
  );
};
